<template>
  <div
    ref="rootRef"
    class="collapse"
    :data-collapse="state"
    :style="style"
    @transitionend.self="onTransitionEnd"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
type CollapseState = 'collapsed' | 'collapsing' | 'expanded' | 'expanding'

type Props = {
  baseHeight?: number
  when: boolean
}

const props = withDefaults(defineProps<Props>(), {
  baseHeight: 0,
})

const rootRef = useTemplateRef<HTMLDivElement>('rootRef')
const state = ref<CollapseState>(props.when ? 'expanded' : 'collapsed')
const transitionHeight = ref('')

const style = computed(() => {
  switch (state.value) {
    case 'collapsed':
      return {
        display: props.baseHeight > 0 ? undefined : 'none',
        height: `${props.baseHeight}px`,
        overflow: 'hidden',
      }
    case 'collapsing':
    case 'expanding':
      return {
        height: transitionHeight.value,
        overflow: 'hidden',
      }
    default:
      return undefined
  }
})

watch(
  () => props.when,
  async (isExpanded) => {
    const element = rootRef.value
    if (!element) return

    transitionHeight.value = `${element.offsetHeight}px`
    state.value = isExpanded ? 'expanding' : 'collapsing'
    await nextTick()

    const targetHeight = isExpanded ? element.scrollHeight : props.baseHeight
    if (element.offsetHeight === targetHeight) {
      state.value = isExpanded ? 'expanded' : 'collapsed'
      return
    }
    transitionHeight.value = `${targetHeight}px`
  },
)

const onTransitionEnd = (event: TransitionEvent) => {
  if (event.propertyName !== 'height') return

  if (state.value === 'expanding') state.value = 'expanded'
  else if (state.value === 'collapsing') state.value = 'collapsed'
}
</script>

<style lang="scss" scoped>
.collapse {
  transition: height 300ms cubic-bezier(0.33, 1, 0.68, 1);

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 1ms;
  }
}
</style>
